import { useState, useEffect, useMemo, useCallback } from 'react';
import { Dropdown, FormControl, Spinner, FormLabel } from 'react-bootstrap';
import { BsMic, BsChevronDown, BsGlobe } from 'react-icons/bs';
import { languagesService } from '../../api/services';
import { toImageSrc } from '../../utils/imageUrl';

import ukFlag from '../../assets/images/flags/uk.svg';
import grFlag from '../../assets/images/flags/gr.svg';
import frFlag from '../../assets/images/flags/fr.svg';
import spFlag from '../../assets/images/flags/sp.svg';
import itFlag from '../../assets/images/flags/it.svg';
import arFlag from '../../assets/images/flags/ar.svg';
import cnFlag from '../../assets/images/flags/cn.svg';
import czFlag from '../../assets/images/flags/cz.svg';
import plFlag from '../../assets/images/flags/pl.svg';

const flagMap = {
  gb: ukFlag,
  en: ukFlag,
  uk: ukFlag,
  gr: grFlag,
  el: grFlag,
  de: grFlag, // placeholder until a dedicated German flag is provided
  fr: frFlag,
  cz: czFlag,
  pl: plFlag,
  es: spFlag,
  it: itFlag,
  ar: arFlag,
  cn: cnFlag,
  zh: cnFlag
};

const getFlagImage = (code) => {
  if (!code) return null;
  return flagMap[code.toLowerCase()] || null;
};

const renderFlag = (language) => {
  if (!language) {
    return (
      <span className="language-select-flag fallback">
        <BsGlobe size={12} />
      </span>
    );
  }

  const image = getFlagImage(language.code);
  if (image) {
    return (
      <span className="language-select-flag">
        <img src={toImageSrc(image)} alt={language.name} />
      </span>
    );
  }

  const flagEmojiMap = {
    gb: '🇬🇧',
    en: '🇬🇧',
    uk: '🇬🇧',
    fr: '🇫🇷',
    cz: '🇨🇿',
    pl: '🇵🇱',
    gr: '🇬🇷',
    el: '🇬🇷',
    es: '🇪🇸',
    it: '🇮🇹',
    cn: '🇨🇳',
    zh: '🇨🇳'
  };

  return (
    <span className="language-select-flag fallback">
      {flagEmojiMap[language.code?.toLowerCase()] || <BsGlobe size={12} />}
    </span>
  );
};

const LanguageSelect = ({
  value,
  defaultValue = null,
  onChange,
  className = '',
  placeholder = 'Language',
  showIcon = true,
  iconSize = 16,
  showLabel = false,
  labelText = 'Language',
  allOptionLabel = 'All Languages'
}) => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setLoading(true);
        const response = await languagesService.getAll();
        if (response.data?.data) {
          setLanguages(response.data.data);
        } else if (Array.isArray(response.data)) {
          setLanguages(response.data);
        } else {
          setLanguages([]);
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
        setLanguages([
          { id: 1, name: 'English', code: 'gb' },
          { id: 2, name: 'French', code: 'fr' },
          { id: 3, name: 'German', code: 'de' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value ?? null);
    }
  }, [value]);

  const selectedValue = value !== undefined ? value : internalValue;

  const selectedLanguage = useMemo(
    () => {
      if (selectedValue == null || selectedValue === '' || selectedValue === '-1') return null;
      const raw = String(selectedValue).toLowerCase();
      return languages.find((lang) => {
        const code = String(lang.code || '').toLowerCase();
        return code === raw || String(lang.id) === String(selectedValue);
      }) || null;
    },
    [languages, selectedValue]
  );

  useEffect(() => {
    if (!selectedLanguage?.code || selectedValue == null) return;
    const code = String(selectedLanguage.code).toLowerCase();
    if (String(selectedValue).toLowerCase() === code) return;
    if (value === undefined) {
      setInternalValue(code);
    }
    onChange?.(code);
  }, [selectedLanguage, selectedValue, onChange, value]);

  const filteredLanguages = useMemo(() => {
    if (!searchTerm) return languages;
    const term = searchTerm.toLowerCase();
    return languages.filter((lang) => lang.name.toLowerCase().includes(term));
  }, [languages, searchTerm]);

  const handleSelectLanguage = useCallback(
    (language) => {
      const next = language?.code
        ? String(language.code).toLowerCase()
        : (language?.id != null ? String(language.id) : null);
      if (value === undefined) {
        setInternalValue(next);
      }
      onChange?.(next);
      setIsOpen(false);
      setSearchTerm('');
    },
    [value, onChange]
  );

  const handleToggle = useCallback(
    (nextShow) => {
      setIsOpen(nextShow);
      if (!nextShow) {
        setSearchTerm('');
      }
    },
    []
  );

  const renderOption = (language) => {
    const isActive =
      language && selectedLanguage
        ? String(language.id) === String(selectedLanguage.id)
        : false;

    return (
      <button
        type="button"
        key={language.id}
        className={`language-option ${isActive ? 'active' : ''}`}
        onClick={() => handleSelectLanguage(language)}
      >
        {renderFlag(language)}
        <span>{language.name}</span>
      </button>
    );
  };

  return (
    <div
      className={`language-select-wrapper ${showLabel ? 'form-control-border' : ''}`}
      style={{ minWidth: 0, flex: '1 1 0' }}
    >
      {showLabel && <FormLabel className="form-label">{labelText}</FormLabel>}

      <Dropdown
        show={isOpen}
        onToggle={handleToggle}
        className="language-select-dropdown w-100"
      >
        <Dropdown.Toggle
          as="button"
          type="button"
          className={`language-select-toggle ${className}`}
        >
          <div className="language-select-value">
            {selectedLanguage ? renderFlag(selectedLanguage) : renderFlag(null)}
            <span className={selectedLanguage ? '' : 'text-muted'}>
              {selectedLanguage ? selectedLanguage.name : placeholder}
            </span>
          </div>
          <BsChevronDown className="language-select-caret" size={14} />
        </Dropdown.Toggle>
        <Dropdown.Menu className="language-select-menu p-0 w-100">
          <div className="language-select-search px-3 py-2 border-bottom">
            <FormControl
              size="sm"
              placeholder="Search language"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="language-select-list">
            <button
              type="button"
              className={`language-option ${!selectedLanguage ? 'active' : ''}`}
              onClick={() => handleSelectLanguage(null)}
            >
              {renderFlag(null)}
              <span>{allOptionLabel}</span>
            </button>

            {loading ? (
              <div className="language-select-empty py-3 text-center text-muted">
                <Spinner animation="border" size="sm" />
              </div>
            ) : filteredLanguages.length > 0 ? (
              filteredLanguages.map(renderOption)
            ) : (
              <div className="language-select-empty px-3 py-2 text-muted">
                No languages found
              </div>
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>

      {showIcon && (
        <span className="form-icon">
          <BsMic className="text-primary flex-shrink-0" size={iconSize} />
        </span>
      )}
    </div>
  );
};

export default LanguageSelect;

